/* WOKORA v6.6 — progressive, accessibility-aware storefront motion */
(() => {
  'use strict';
  if (window.__wokoraMotionV66) return;
  window.__wokoraMotionV66 = true;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const EASE = 'cubic-bezier(.22,1,.36,1)';
  const enhanced = new WeakSet();
  const activeAnimations = new Set();
  let observer = null;

  const trackAnimation = animation => {
    if (!animation) return animation;
    activeAnimations.add(animation);
    animation.onfinish = () => {
      activeAnimations.delete(animation);
      animation.cancel();
    };
    animation.oncancel = () => activeAnimations.delete(animation);
    return animation;
  };

  const visibleRect = el => {
    if (!(el instanceof Element) || el.closest('[hidden],[aria-hidden="true"]')) return false;
    const rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  };

  const siblingIndex = el => {
    const parent = el.parentElement;
    if (!parent) return 0;
    const children = Array.from(parent.children).filter(child => child.getClientRects().length);
    const index = children.indexOf(el);
    return index < 0 ? 0 : index;
  };

  const delayFor = (el, step = 0, cap = 280) => step ? Math.min((siblingIndex(el) % 7) * step, cap) : 0;

  const immediateAnimation = (el, direction, delay) => {
    if (reduceMotion.matches || typeof el.animate !== 'function') return;
    let transform = 'translate3d(0,14px,0)';
    if (direction === 'left') transform = 'translate3d(-14px,0,0)';
    if (direction === 'right') transform = 'translate3d(14px,0,0)';
    if (direction === 'scale') transform = 'translate3d(0,8px,0) scale(.987)';
    trackAnimation(el.animate(
      [{ opacity: 0, transform }, { opacity: 1, transform: 'none' }],
      { duration: 560, delay, easing: EASE, fill: 'both' }
    ));
  };

  const ensureObserver = () => {
    if (observer || !('IntersectionObserver' in window) || reduceMotion.matches) return observer;
    observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    return observer;
  };

  const enhanceReveal = (el, config = {}) => {
    if (!(el instanceof Element) || enhanced.has(el) || !visibleRect(el)) return;
    enhanced.add(el);
    if (reduceMotion.matches) return;

    const direction = config.direction || 'up';
    const delay = delayFor(el, config.stagger || 0, config.delayCap || 280);
    const rect = el.getBoundingClientRect();
    const viewport = window.innerHeight || document.documentElement.clientHeight;
    if (rect.bottom < 0) return;
    if (rect.top < viewport * 0.9) {
      immediateAnimation(el, direction, delay);
      return;
    }

    const io = ensureObserver();
    if (!io) return;
    el.classList.add('wk-motion-reveal');
    el.dataset.wkMotionDirection = direction;
    el.style.setProperty('--wk-motion-delay', `${delay}ms`);
    io.observe(el);
  };

  const revealGroups = [
    { selector: '.wk-cats__head', direction: 'up' },
    { selector: 'main .section-heading', direction: 'up' },
    { selector: 'main [data-product-card]', direction: 'scale', stagger: 44, delayCap: 220 },
    { selector: '.wk-biz__copy', direction: 'left' },
    { selector: '.wk-biz__item', direction: 'right', stagger: 65, delayCap: 220 },
    { selector: '.collection-hero,.search-header,.list-collections-header', direction: 'up' },
    { selector: '.template-collection .facets,.template-search .facets', direction: 'left' },
    { selector: '.template-product .product-gallery', direction: 'left' },
    { selector: '.template-product .product-info', direction: 'right' },
    { selector: '.template-product .product-details', direction: 'up' },
    { selector: '.template-cart .cart-items', direction: 'left' },
    { selector: '.template-cart .cart-summary', direction: 'right' },
    { selector: '.wokora-faq__item', direction: 'up', stagger: 42, delayCap: 170 },
    { selector: '.business-v233__hero', direction: 'up' },
    { selector: '.business-v233__card', direction: 'scale', stagger: 48, delayCap: 190 },
    { selector: '.w37-delivery-card,.w37-model-card,.w37-process-card', direction: 'scale', stagger: 46, delayCap: 180 },
    { selector: '.wk-footer__payments', direction: 'up' },
    { selector: '.wk-footer__newsletter', direction: 'scale' },
    { selector: '.wk-footer__brand-row', direction: 'up' },
    { selector: '.wk-footer__menu', direction: 'up', stagger: 55, delayCap: 165 },
    { selector: '.wk-footer__bottom', direction: 'up' }
  ];

  const processRoot = root => {
    if (!(root instanceof Element) && root !== document) return;
    revealGroups.forEach(config => {
      if (root instanceof Element && root.matches(config.selector)) enhanceReveal(root, config);
      root.querySelectorAll?.(config.selector).forEach(el => enhanceReveal(el, config));
    });
  };

  const animateHero = root => {
    if (reduceMotion.matches) return;
    const heroes = root instanceof Element && root.matches('.w65-intro') ? [root] : Array.from(root.querySelectorAll?.('.w65-intro') || []);
    heroes.forEach(hero => {
      if (hero.dataset.wkMotionHero === 'true') return;
      hero.dataset.wkMotionHero = 'true';
      const slide = hero.querySelector('.w65-intro__slide.is-active') || hero.querySelector('.w65-intro__slide');
      if (!slide) return;

      const sequence = [
        ['.w65-intro__accent', 40, 500],
        ['.w65-intro__eyebrow', 90, 520],
        ['.w65-intro__title', 145, 680],
        ['.w65-intro__lead', 220, 610],
        ['.w65-intro__actions', 290, 560],
        ['.w65-intro__small', 350, 500]
      ];

      sequence.forEach(([selector, delay, duration]) => {
        const el = slide.querySelector(selector);
        if (!el || typeof el.animate !== 'function') return;
        trackAnimation(el.animate(
          [{ opacity: 0, transform: 'translate3d(0,13px,0)' }, { opacity: 1, transform: 'none' }],
          { duration, delay, easing: EASE, fill: 'both' }
        ));
      });

      const image = slide.querySelector('.w65-intro__media img');
      if (image && typeof image.animate === 'function') {
        trackAnimation(image.animate(
          [{ opacity: .82, transform: 'scale(1.022)' }, { opacity: 1, transform: 'scale(1)' }],
          { duration: 980, delay: 80, easing: EASE, fill: 'both' }
        ));
      }

      slide.querySelectorAll('.w65-intro__proof').forEach((proof, index) => {
        if (typeof proof.animate !== 'function') return;
        trackAnimation(proof.animate(
          [{ opacity: 0, transform: 'translate3d(0,8px,0)' }, { opacity: 1, transform: 'none' }],
          { duration: 480, delay: 420 + index * 70, easing: EASE, fill: 'both' }
        ));
      });
    });
  };

  const process = root => {
    processRoot(root);
    animateHero(root);
  };

  const eventRoot = event => event?.detail?.root instanceof Element ? event.detail.root : document;

  const bumpCartBadges = () => {
    if (reduceMotion.matches) return;
    document.querySelectorAll('[data-cart-count]').forEach(badge => {
      badge.classList.remove('wk-cart-bump');
      void badge.offsetWidth;
      badge.classList.add('wk-cart-bump');
      window.setTimeout(() => badge.classList.remove('wk-cart-bump'), 420);
    });
  };

  const disableActiveMotion = () => {
    observer?.disconnect();
    observer = null;
    activeAnimations.forEach(animation => animation.cancel());
    activeAnimations.clear();
    document.querySelectorAll('.wk-motion-reveal').forEach(el => {
      el.classList.add('is-visible');
      el.style.removeProperty('--wk-motion-delay');
    });
    document.querySelectorAll('[data-cart-count]').forEach(badge => badge.classList.remove('wk-cart-bump'));
  };

  const setup = () => {
    process(document);
    document.addEventListener('shopify:section:load', event => process(event.target instanceof Element ? event.target : document));
    document.addEventListener('wokora:content-updated', event => process(eventRoot(event)));
    document.addEventListener('wokora:cart-drawer-refreshed', event => process(eventRoot(event)));
    document.addEventListener('wokora:cart-updated', bumpCartBadges);
    document.addEventListener('wokora:cart-v73', bumpCartBadges);
    if (reduceMotion.addEventListener) {
      reduceMotion.addEventListener('change', event => {
        if (event.matches) disableActiveMotion();
      });
    } else if (reduceMotion.addListener) {
      reduceMotion.addListener(event => {
        if (event.matches) disableActiveMotion();
      });
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', setup, { once: true });
  else setup();
})();
